import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { boundariesFromAnchors, isDeclaredPage, type RenderedBoundary } from './page-guide';

type Side = 'left' | 'center' | 'right';

/** A logo the renderer stamps into a corner, and the height it prints at. */
export interface RunningImage {
  src: string;
  maxHeightPt: number;
}

export interface RunningSlots {
  left: string;
  center: string;
  right: string;
  images?: Partial<Record<Side, RunningImage>>;
}

/**
 * What the renderer puts around a page: the margin and the running header and footer. It
 * lives in @page rules, which no screen ever applies, so the editor draws it on each sheet
 * itself. Slot text keeps the {page} and {pages} tokens the renderer fills.
 */
export interface PageFurniture {
  size?: string;
  margins: { topIn: number; rightIn: number; bottomIn: number; leftIn: number };
  header: RunningSlots;
  footer: RunningSlots;
  /** The @page fill: the paper's color wherever the document paints nothing. */
  background?: string;
  /** Whether the renderer prints "N of M" bottom-right when nothing else claims that corner. */
  autoPageNumber: boolean;
  bandTableOrdinals?: number[];
}

/** The sheets the render printed, from its page map. */
export interface MeasuredPages {
  pageCount: number;
  boundaries: RenderedBoundary[];
}

interface PageFurnitureState {
  furniture: PageFurniture | null;
  measured: MeasuredPages | null;
  decorations: DecorationSet;
  tables: DecorationSet;
}

const furnitureKey = new PluginKey<PageFurnitureState>('pageFurniture');
const furnitureUpdateKey = new PluginKey('pageFurnitureUpdate');

// The renderer routes these to a margin-0 page with no margin boxes.
const FULL_BLEED = /(?:^|\s)(?:cara-cover|cara-closing|cover|closing)(?:\s|$)/;

/** The renderer's own substitution, so the editor prints what the PDF prints. */
export const fillRunningSlot = (text: string, page: number, pages: number) =>
  text.replace(/\{page\}/g, String(page)).replace(/\{pages\}/g, String(pages));

interface Page {
  pos: number;
  node: ProseMirrorNode;
  fullBleed: boolean;
}

/**
 * The document's pages in order. A full-bleed page draws no furniture but still counts: the
 * printed page counter does not skip covers.
 */
const pagesOf = (doc: ProseMirrorNode): Page[] => {
  const out: Page[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== 'section') return true;
    if (isDeclaredPage(node)) {
      out.push({ pos, node, fullBleed: FULL_BLEED.test(String(node.attrs.class)) });
    }
    return false;
  });
  return out;
};

const band = (kind: 'header' | 'footer', slots: RunningSlots) => {
  const el = document.createElement('div');
  el.className = `cara-running cara-running-${kind}`;
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('aria-hidden', 'true');
  for (const side of ['left', 'center', 'right'] as const) {
    const span = document.createElement('span');
    span.className = `cara-running-${side}`;
    const image = slots.images?.[side];
    if (image) {
      const img = document.createElement('img');
      img.src = image.src;
      img.alt = '';
      img.style.height = `${image.maxHeightPt}pt`;
      // The PDF leaves a corner whose logo cannot be drawn empty.
      img.addEventListener('error', () => img.remove());
      span.appendChild(img);
    } else {
      span.textContent = slots[side];
    }
    el.appendChild(span);
  }
  return el;
};

const filled = (slots: RunningSlots, page: number, pages: number): RunningSlots => ({
  left: fillRunningSlot(slots.left, page, pages),
  center: fillRunningSlot(slots.center, page, pages),
  right: fillRunningSlot(slots.right, page, pages),
  images: slots.images
});

const bandKey = (slots: RunningSlots) =>
  (['left', 'center', 'right'] as const)
    .map((side) => {
      const image = slots.images?.[side];
      return image ? `${image.src}@${image.maxHeightPt}` : slots[side];
    })
    .join('|');

interface Numbered extends Page {
  first: number;
  last: number;
}

/**
 * Each declared page with the sheets it prints on. A declared page that overflows spills onto
 * further sheets, so once the render printed more sheets than the document declares, the
 * numbers come from where the render broke rather than from counting sections.
 */
const numberPages = (
  doc: ProseMirrorNode,
  pages: Page[],
  measured: MeasuredPages | null
): { numbered: Numbered[]; total: number } => {
  if (!measured || measured.pageCount <= pages.length) {
    return {
      numbered: pages.map((page, index) => ({ ...page, first: index + 1, last: index + 1 })),
      total: pages.length
    };
  }
  const breaks = boundariesFromAnchors(doc, measured.boundaries);
  let next = 1;
  const numbered = pages.map((page) => {
    const end = page.pos + page.node.nodeSize;
    const first = breaks.find((b) => b.pos === page.pos)?.page ?? next;
    const inner = breaks.filter((b) => b.pos > page.pos && b.pos < end).map((b) => b.page);
    const last = Math.max(first, ...inner);
    next = last + 1;
    return { ...page, first, last };
  });
  return { numbered, total: Math.max(measured.pageCount, next - 1) };
};

const buildDecorations = (
  doc: ProseMirrorNode,
  furniture: PageFurniture | null,
  measured: MeasuredPages | null
): DecorationSet => {
  if (!furniture) return DecorationSet.empty;
  const { numbered, total } = numberPages(doc, pagesOf(doc), measured);
  const decorations: Decoration[] = [];
  numbered.forEach(({ pos, node, fullBleed, first, last }) => {
    if (fullBleed) return;
    const header = filled(furniture.header, first, total);
    const footer = filled(furniture.footer, last, total);
    if (furniture.autoPageNumber && !footer.right) {
      footer.right = `${last} of ${total}`;
    }
    // Siblings of the sheet, not children: a document may clip its pages with overflow, and
    // the bands sit in the margin, which is outside anything the page can clip.
    decorations.push(
      Decoration.widget(pos, () => band('header', header), {
        side: -1,
        key: `furniture-header-${first}-${bandKey(header)}`
      }),
      Decoration.widget(pos + node.nodeSize, () => band('footer', footer), {
        side: 1,
        key: `furniture-footer-${last}-${bandKey(footer)}`
      })
    );
  });
  return DecorationSet.create(doc, decorations);
};

const bandTableDecorations = (doc: ProseMirrorNode, ordinals: number[] = []): DecorationSet => {
  const marked = new Set(ordinals);
  const decorations: Decoration[] = [];
  let ordinal = 0;
  doc.descendants((node, pos) => {
    if (node.type.name !== 'table') return;
    if (marked.has(ordinal)) {
      decorations.push(Decoration.node(pos, pos + node.nodeSize, { 'data-cara-on-band': '' }));
    }
    ordinal += 1;
  });
  return DecorationSet.create(doc, decorations);
};

export const setPageFurniture = (editor: Editor, furniture: PageFurniture | null) => {
  if (!editor.view || editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(furnitureUpdateKey, { furniture }));
};

/** Hands the furniture the render's page map, or null when the renderer has not answered. */
export const setMeasuredPages = (editor: Editor, measured: MeasuredPages | null) => {
  if (!editor.view || editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(furnitureUpdateKey, { measured }));
};

export const PageFurnitureExtension = Extension.create({
  name: 'pageFurniture',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: furnitureKey,
        state: {
          init: () =>
            ({
              furniture: null,
              measured: null,
              decorations: DecorationSet.empty,
              tables: DecorationSet.empty
            }) as PageFurnitureState,
          apply(tr, value): PageFurnitureState {
            const update = tr.getMeta(furnitureUpdateKey) as
              | Partial<Pick<PageFurnitureState, 'furniture' | 'measured'>>
              | undefined;
            if (update !== undefined) {
              const next = { ...value, ...update };
              return {
                ...next,
                decorations: buildDecorations(tr.doc, next.furniture, next.measured),
                tables:
                  update.furniture !== undefined
                    ? bandTableDecorations(tr.doc, next.furniture?.bandTableOrdinals)
                    : value.tables.map(tr.mapping, tr.doc)
              };
            }
            // Adding or removing a page renumbers every page after it.
            if (tr.docChanged && value.furniture) {
              return {
                ...value,
                decorations: buildDecorations(tr.doc, value.furniture, value.measured),
                tables: tr.getMeta('preventUpdate')
                  ? bandTableDecorations(tr.doc, value.furniture.bandTableOrdinals)
                  : value.tables.map(tr.mapping, tr.doc)
              };
            }
            return value;
          }
        },
        props: {
          decorations(state) {
            const value = furnitureKey.getState(state);
            return value?.decorations.add(state.doc, value.tables.find());
          }
        }
      })
    ];
  }
});

/** Dimensions accepted by the template page-size control, including CSS lengths. */
export const pageDimensions = (size = 'Letter'): { width: string; height: string } => {
  const sizes: Record<string, [string, string]> = {
    letter: ['8.5in', '11in'],
    legal: ['8.5in', '14in'],
    tabloid: ['11in', '17in'],
    a3: ['297mm', '420mm'],
    a4: ['210mm', '297mm'],
    a5: ['148mm', '210mm']
  };
  const parts = size.trim().toLowerCase().split(/\s+/);
  const landscape = parts.includes('landscape');
  const paper = parts.filter((part) => part !== 'landscape' && part !== 'portrait');
  const validLength = (value: string) =>
    /^(?:\d+(?:\.\d+)?|\.\d+)(?:in|cm|mm|pt|pc|px)$/.test(value);
  const dimensions =
    sizes[paper[0]] ??
    (paper.length > 0 && paper.length <= 2 && paper.every(validLength)
      ? [paper[0], paper[1] ?? paper[0]]
      : sizes.letter);
  const [width, height] = landscape ? [dimensions[1], dimensions[0]] : dimensions;
  return { width, height };
};
